import { useEffect, useState } from 'react';
import { useAuth } from '@erpsquad/common/runtime';
import { auth } from '../constants/auth';

/**
 * In MFE mode the AuthContext of the common-hub is not bridged from the
 * host app, so `useAuth()` returns `user: null`. As a fallback we read
 * the persisted user object directly from localStorage so that default
 * values (e.g. company_id) are always available.
 */
const getEffectiveUser = (contextUser: any): any => {
    if (contextUser) return contextUser;
    try {
        const stored = window.localStorage.getItem(auth.storageUserKeyName);
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
};

const setCompany = (user: any) => {
    return {
        value: user?.company_id,
        options: [{
            // Fallback to company_id when company_data is not present in the
            // user object (common in MFE where the stored payload is leaner).
            label: user?.company_data?.name ?? user?.company_id,
            value: user?.company_id
        }]
    }
}

const setCountry = (user: any) => {
    return {
        value: 231
    }
}

const setTimezone = (user: any) => {
    return {
        value: 371 
    }
}

export const configMapper: Record<string, any> = {
    'company': {
        callBack: setCompany,
    },
    'company_id': {
        callBack: setCompany,
    },
    'country_id': {
        callBack: setCountry,
    },
    'countries': {
        callBack: setCountry,
    },
    'timezone': {
        callBack: setTimezone,
    },
    'timezone_id': {
        callBack: setTimezone,
    },
}

export const getDefaultConfig = (props: any, user: any) => {
    const { apiType, name } = props;
    const config = configMapper[apiType || name];
    if (config) {
        return config.callBack(user)
    }
    return {
        value: null,
        options: []
    }
}

export const useDefaultConfig = ({
    props,
    setValue,
    isInternalOptions,
    watchedValue
}: {
    props: any;
    setValue?: any;
    isInternalOptions: boolean;
    watchedValue: any;
}) => {
    const { user: authUser } = useAuth();
    const contextUser = getEffectiveUser(authUser);
    const [defaultOptions, setDefaultOptions] = useState<any>([]);

    useEffect(() => {
        const user = getEffectiveUser(contextUser);
        const { value, options } = getDefaultConfig(props, user);

        if (!isInternalOptions) {
            setDefaultOptions(options);
        }

        // If field already has a value, do not override it with default
        const hasValue = watchedValue !== null && watchedValue !== undefined && watchedValue !== "" && (!Array.isArray(watchedValue) || watchedValue.length > 0);
        if (hasValue) {
            return;
        }

        if (value === null || value === undefined) {
            return;
        }

        if (typeof setValue === 'function') {
            setValue(`${props.fieldArrayName}.${props.name}`, props?.is_multiselect ? [value] : value);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.name, props?.apiType, contextUser, setValue, isInternalOptions]);

    return {
        defaultOptions
    }
}